
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TokenUpdateNotify struct
 */
class TokenUpdateNotify {
  /**
   * Initialize struct
   * @param {!Array=} deletedTokenIds
   * @constructor
   */
  constructor (deletedTokenIds = []) {
    this.DeletedTokenIds = deletedTokenIds
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TokenUpdateNotify}
   * @param {!TokenUpdateNotify} other Other struct
   * @returns {!TokenUpdateNotify} This struct
   */
  copy (other) {
    if (other.DeletedTokenIds != null) {
      this.DeletedTokenIds = []
      for (let item of other.DeletedTokenIds) {
        if (item != null) {
          let tempItem
          tempItem = UInt64.fromNumber(item)
          this.DeletedTokenIds.push(tempItem)
        } else {
          this.DeletedTokenIds.push(null)
        }
      }
    } else {
      this.DeletedTokenIds = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TokenUpdateNotify}
   * @returns {!TokenUpdateNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TokenUpdateNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TokenUpdateNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TokenUpdateNotify}
   * @param {!TokenUpdateNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TokenUpdateNotify)) {
      throw new TypeError('Instance of TokenUpdateNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TokenUpdateNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DeletedTokenIds: ((this.DeletedTokenIds != null) ? Array.from(this.DeletedTokenIds, item => ((item != null) ? item.toNumber() : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TokenUpdateNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TokenUpdateNotify} other Object value
   * @returns {!TokenUpdateNotify} Created struct
   */
  static fromObject (other) {
    return new TokenUpdateNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TokenUpdateNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TokenUpdateNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TokenUpdateNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 14125
  }
}

export { TokenUpdateNotify };

/**
 * Fast Binary Encoding TokenUpdateNotify field model
 */
class FieldModelTokenUpdateNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DeletedTokenIds = new fbe.FieldModelVector(new fbe.FieldModelUInt64(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelTokenUpdateNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTokenUpdateNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DeletedTokenIds.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTokenUpdateNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DeletedTokenIds.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTokenUpdateNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTokenUpdateNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTokenUpdateNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 14125
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTokenUpdateNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTokenUpdateNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DeletedTokenIds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DeletedTokenIds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DeletedTokenIds.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTokenUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTokenUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTokenUpdateNotify}
   * @param {!TokenUpdateNotify} fbeValue Default value, defaults is new TokenUpdateNotify()
   * @returns {!TokenUpdateNotify} TokenUpdateNotify value
   */
  get (fbeValue = new TokenUpdateNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTokenUpdateNotify}
   * @param {!TokenUpdateNotify} fbeValue TokenUpdateNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DeletedTokenIds.fbeSize) <= fbeStructSize) {
      this.DeletedTokenIds.get(fbeValue.DeletedTokenIds)
    } else {
      fbeValue.DeletedTokenIds.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DeletedTokenIds.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTokenUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTokenUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTokenUpdateNotify}
   * @param {!TokenUpdateNotify} fbeValue TokenUpdateNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTokenUpdateNotify}
   * @param {TokenUpdateNotify} fbeValue TokenUpdateNotify value
   */
  setFields (fbeValue) {
    this.DeletedTokenIds.set(fbeValue.DeletedTokenIds)
  }
}

export { FieldModelTokenUpdateNotify };

/**
 * Fast Binary Encoding TokenUpdateNotify model
 */
class TokenUpdateNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTokenUpdateNotify(this.buffer, 4)
  }

  /**
   * Get the TokenUpdateNotify model
   * @this {!TokenUpdateNotifyModel}
   * @returns {!FieldModelTokenUpdateNotify} model TokenUpdateNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TokenUpdateNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TokenUpdateNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TokenUpdateNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TokenUpdateNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTokenUpdateNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TokenUpdateNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TokenUpdateNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TokenUpdateNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TokenUpdateNotifyModel}
   * @param {!TokenUpdateNotify} value TokenUpdateNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TokenUpdateNotifyModel}
   * @param {!TokenUpdateNotify} value TokenUpdateNotify value, defaults is new TokenUpdateNotify()
   * @return {!object} Deserialized TokenUpdateNotify value and its size
   */
  deserialize (value = new TokenUpdateNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TokenUpdateNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TokenUpdateNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TokenUpdateNotifyModel}
   * @param {!number} prev Previous TokenUpdateNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TokenUpdateNotifyModel };
